import React, { useCallback, useContext } from 'react';
import { FileCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { valueOrDefault } from 'utils/functions';
import PropTypes from 'prop-types';
import { Tooltip, Grid } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { TextField, IconButton, Card, Button } from '@totalsoft/rocket-ui';
import AttachmentContainer from './AttachmentContainer';
import { emptyString } from 'constants/defaultValues';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import FakeTextWrapper from 'components/common/FakeTextWrapper';
import { DetatchedAccordion } from 'components/common/SurfaceStyles';

const AttachmentsComponent = ({
  attachments,
  maxSize,
  availableDocumentTransitions,
  loading,
  error,
  onSave,
  onChangeState,
  onDelete,
  onUploadFile,
  onDownloadFile,
  onExpandedChange,
  expanded,
  onSelectAttachment,
  onOpenRequiredDocTypesModal,
  onToggleDocStateTransitionsModal,
  onToggleHistory,
  loadPropertyDetails
}) => {
  const { t } = useTranslation();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToAddOrRemoveDocuments = useAuthorization(
    AuthorizationCodes.hasRightToAddOrRemoveDocuments,
    documentTypeId,
    documentStateId
  );
  const fileInputRef = React.createRef();

  const handleUploadClick = useCallback(
    documentAttachmentId => {
      fileInputRef.current.documentAttachmentId = documentAttachmentId;
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    },
    [fileInputRef]
  );

  const handleFileSelected = useCallback(
    event => {
      let documentAttachmentId = event.target.documentAttachmentId;
      if (event.target.files.length > 0) {
        let file = event.target.files[0];
        onUploadFile(documentAttachmentId, file);
      }
    },
    [onUploadFile]
  );

  return (
    <Card id='AttachmentsComponent_IconCard' icon={FileCopy} title={t('Offer.Edit.AttachmentsData.Title')}>
      <>
        <input type='file' style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelected} />
        <Grid container spacing={2}>
          {!loading && !error && (
            <>
              {attachments &&
                attachments.map((attachment, idx) => {
                  const cardExpanded = expanded[attachment.documentAttachmentId] ?? false;
                  const availableTransitions = availableDocumentTransitions.filter(
                    x => x.initialStateId === (attachment.stateId |> valueOrDefault(emptyString))
                  );

                  return (
                    <Grid item xs={12} key={idx}>
                      <DetatchedAccordion
                        expanded={cardExpanded}
                        onChange={() => onExpandedChange(attachment.documentAttachmentId)}
                        title={
                          !cardExpanded && (
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={6} sm={4}>
                                <TextField
                                  id={'attachment_DocumentName_' + idx}
                                  fullWidth
                                  disabled
                                  name='requiredDocumentName'
                                  value={attachment.requiredDocumentName |> valueOrDefault(emptyString)}
                                  label={t('Offer.Edit.AttachmentsData.DocumentName')}
                                />
                              </Grid>
                              <Grid item xs={12} md={3} sm={4}>
                                <TextField
                                  id={'attachment_Entity_' + idx}
                                  fullWidth
                                  disabled
                                  name='entity'
                                  value={`${attachment.entityType |> valueOrDefault(emptyString)} ${
                                    attachment.entityTitle |> valueOrDefault(emptyString)
                                  }`}
                                  label={t('Offer.Edit.AttachmentsData.Entity')}
                                />
                              </Grid>
                              <Grid item xs={10} md={2} sm={3}>
                                {attachment.stateId && (
                                  <TextField
                                    id={'attachment_State_' + idx}
                                    fullWidth
                                    disabled
                                    name='stateName'
                                    value={attachment.stateName |> valueOrDefault(emptyString)}
                                    label={t('Offer.Edit.AttachmentsData.State')}
                                  />
                                )}
                              </Grid>
                              <Grid item container xs={2} md={1} sm={1} justifyContent={'flex-end'}>
                                {attachment.fileUniqueId && (
                                  <Tooltip title={t('Offer.Edit.AttachmentsData.Uploaded')}>
                                    <span>
                                      <IconButton id={'attachment_UploadedBtn_' + idx}>
                                        <CloudUploadIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Grid>
                            </Grid>
                          )
                        }
                        content={
                          <AttachmentContainer
                            key={idx}
                            attachment={attachment}
                            maxSize={maxSize}
                            availableTransitions={availableTransitions}
                            onSave={onSave}
                            onChangeState={onChangeState}
                            onDelete={onDelete}
                            onUploadFile={handleUploadClick}
                            onDownloadFile={onDownloadFile}
                            onOpenDocStateTransitionsModal={attachment => {
                              onSelectAttachment(attachment);
                              onToggleDocStateTransitionsModal(true);
                            }}
                            onOpenHistoryModal={attachment => {
                              onSelectAttachment(attachment);
                              onToggleHistory(true);
                            }}
                            loadPropertyDetails={loadPropertyDetails}
                          />
                        }
                      />
                      {/* <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          IconButtonProps={{ onClick: () => onExpandedChange(attachment.documentAttachmentId) }}
                        >
                          
                        </AccordionSummary> */}
                    </Grid>
                  );
                })}
            </>
          )}
          {loading && <FakeTextWrapper sx={{ ml: 2 }} />}
          <Grid item xs={12}>
            <Button
              color={'primary'}
              disabled={globalReadOnly({
                controlName: 'Documents',
                hasAuthorization: hasRightToAddOrRemoveDocuments
              })}
              onClick={() => onOpenRequiredDocTypesModal(true)}
            >
              {t('General.Add')}
            </Button>
          </Grid>
        </Grid>
      </>
    </Card>
  );
};

AttachmentsComponent.propTypes = {
  attachments: PropTypes.array.isRequired,
  maxSize: PropTypes.number,
  availableDocumentTransitions: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onChangeState: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUploadFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onExpandedChange: PropTypes.func.isRequired,
  expanded: PropTypes.object,
  onSelectAttachment: PropTypes.func.isRequired,
  onOpenRequiredDocTypesModal: PropTypes.func.isRequired,
  onToggleDocStateTransitionsModal: PropTypes.func.isRequired,
  onToggleHistory: PropTypes.func.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired
};

export default AttachmentsComponent;
